/* CookiesPage — /cookies
 *
 * Reflects exactly the cookies HyreDocs sets today. Update the
 * Analytics row when/if you add Plausible / GA / PostHog. The
 * "Open cookie preferences" button reopens the consent banner.
 */
import LegalLayout from '../../components/legal/LegalLayout';
import { Button } from '../../components/ui/button';

function openConsentBanner() {
  // The CookieConsentBanner listens for this custom event and re-opens
  // its preference panel so users can change their choice at any time.
  window.dispatchEvent(new CustomEvent('hyredocs:open-cookie-preferences'));
}

export default function CookiesPage() {
  return (
    <LegalLayout title="Cookie Policy" lastUpdated="8 February 2026" activePath="/cookies">
      <p>
        This policy explains what cookies and similar technologies HyreDocs uses, why we use them, and how you can control them. It supplements our main <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>1. What is a cookie?</h2>
      <p>
        A cookie is a small text file a website saves on your device to remember your choices and keep you signed in. Some are essential to make the site work, others are used to understand how you use the site so we can improve it.
      </p>

      <h2>2. The cookies we use</h2>

      <h3>Strictly necessary (always on)</h3>
      <p>
        These keep you signed in and protect your session. They cannot be turned off because the service won&apos;t work without them.
      </p>
      <table>
        <thead>
          <tr><th>Cookie name</th><th>Purpose</th><th>Expiry</th></tr>
        </thead>
        <tbody>
          <tr><td><code>access_token</code></td><td>HttpOnly auth session for signed-in users</td><td>Session / up to 8 hours</td></tr>
          <tr><td><code>refresh_token</code></td><td>HttpOnly refresh of the auth session</td><td>30 days</td></tr>
          <tr><td><code>hyredocs_cookie_consent</code></td><td>Remembers your cookie preferences on this site</td><td>12 months</td></tr>
          <tr><td><code>tenant_slug</code></td><td>Routes you to your agency&apos;s workspace after sign-in</td><td>Session</td></tr>
        </tbody>
      </table>

      <h3>Analytics (optional, off by default)</h3>
      <p>
        These help us understand which pages people use most so we can improve the product. We only set these if you give consent in the banner.
      </p>
      <table>
        <thead>
          <tr><th>Cookie name</th><th>Provider</th><th>Purpose</th><th>Expiry</th></tr>
        </thead>
        <tbody>
          <tr><td>—</td><td>Reserved for future privacy-friendly analytics (e.g. Plausible)</td><td>Aggregate visit stats — no cross-site tracking</td><td>up to 12 months</td></tr>
        </tbody>
      </table>

      <h2>3. We do not use</h2>
      <ul>
        <li>Third-party advertising or retargeting cookies.</li>
        <li>Cross-site behavioural tracking.</li>
        <li>Social-media share-button cookies (we link to email instead).</li>
      </ul>

      <h2>4. Your choices</h2>
      <p>
        You can change your cookie preferences at any time:
      </p>
      <p>
        <Button onClick={openConsentBanner} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="open-cookie-prefs">
          Open cookie preferences
        </Button>
      </p>
      <p>
        You can also block cookies in your browser settings. Note that strictly necessary cookies cannot be blocked without breaking sign-in functionality.
      </p>

      <h2>5. Changes to this policy</h2>
      <p>
        If we add new categories of cookies we&apos;ll update this page and re-prompt you for consent before any new optional cookie is set.
      </p>

      <h2>6. Contact</h2>
      <p>
        Cookie questions: <a href="mailto:privacy@hyredocs.com">privacy@hyredocs.com</a>.
      </p>
    </LegalLayout>
  );
}
